(function () {
  const DESKTOP_MQ = '(min-width: 990px) and (min-height: 601px)';
  const REDUCED_MQ = '(prefers-reduced-motion: reduce)';
  const FEATURED_SELECTOR = '#MainContent .shopify-section.section-featured-collection';
  const SNAP_DELAY_MS = 140;
  const SNAP_PIXEL_GATE = 2;
  const SNAP_DURATION_MIN_MS = 280;
  const SNAP_DURATION_MAX_MS = 550;
  const SCRUB_LERP = 0.22;
  const SCRUB_EPSILON = 0.0004;
  const Y_EPSILON = 0.25;
  const VELOCITY_REF_PX = 1800;
  const DIRECTIONAL_VELOCITY_PX = 80;

  /** @type {{ destroy: () => void } | null} */
  let activeSession = null;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function easeOutCubic(t) {
    const p = 1 - t;
    return 1 - p * p * p;
  }

  function prefersReducedMotion() {
    return window.matchMedia(REDUCED_MQ).matches;
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function isIndexTemplate() {
    return document.body.classList.contains('template-index');
  }

  function isDesignMode() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  function measureAttachedBaseboardHeight() {
    const featured = document.querySelectorAll(FEATURED_SELECTOR);
    const lastPanel = featured.length ? featured[featured.length - 1] : null;
    if (!lastPanel) return 0;
    const board = lastPanel.querySelector('.section-baseboard');
    if (!board) return 0;
    return Math.round(board.offsetHeight) || 0;
  }

  function computeLatchPx(baseboardHeight) {
    /* Full baseboard segment: idle anywhere in trailing Y snaps to y = 0. */
    return Math.max(0, baseboardHeight);
  }

  function computeExitHoldPx() {
    /* No post-baseboard dwell — pin ends when trailing Y completes. */
    return 0;
  }

  function publishPinHeightVars(baseboardHeight, exitHold, pinRoot) {
    const bbValue = Math.max(0, Math.round(baseboardHeight)) + 'px';
    document.body.style.setProperty('--homepage-sidescroll-baseboard-height', bbValue);
    document.body.style.removeProperty('--homepage-sidescroll-exit-hold');
    document.body.style.removeProperty('--homepage-sidescroll-grit-panel-height');
    const root =
      pinRoot || document.querySelector('#MainContent [data-homepage-sidescroll]');
    if (root) {
      root.style.setProperty('--homepage-sidescroll-baseboard-height', bbValue);
      root.style.removeProperty('--homepage-sidescroll-exit-hold');
    }
  }

  function clearPinHeightVars(pinRoot) {
    document.body.style.removeProperty('--homepage-sidescroll-baseboard-height');
    document.body.style.removeProperty('--homepage-sidescroll-exit-hold');
    document.body.style.removeProperty('--homepage-sidescroll-grit-panel-height');
    const root =
      pinRoot || document.querySelector('#MainContent [data-homepage-sidescroll]');
    if (root) {
      root.style.removeProperty('--homepage-sidescroll-baseboard-height');
      root.style.removeProperty('--homepage-sidescroll-exit-hold');
    }
  }

  /**
   * Publish attached baseboard height for pin root CSS and grit continuity.
   * Prefer last featured panel's .section-baseboard (no post-pin sibling).
   */
  function syncGritSpanVars(pinRoot, exitHold) {
    if (!isIndexTemplate()) return;

    const featured = document.querySelectorAll(FEATURED_SELECTOR);
    const lastPanel = featured.length ? featured[featured.length - 1] : null;

    if (!lastPanel || !lastPanel.querySelector('.section-baseboard')) {
      clearPinHeightVars(pinRoot);
      return;
    }

    const hold =
      typeof exitHold === 'number' ? exitHold : computeExitHoldPx();
    publishPinHeightVars(measureAttachedBaseboardHeight(), hold, pinRoot);
  }

  function travelMetrics(root, baseboardHeight, exitHold) {
    const totalTravel = Math.max(1, root.offsetHeight - window.innerHeight);
    const bb = Math.max(0, baseboardHeight);
    const hold = Math.max(0, exitHold);
    const horizTravel = Math.max(1, totalTravel - bb - hold);
    const latch = computeLatchPx(bb);
    const latchEnd = horizTravel + latch;
    return {
      totalTravel: totalTravel,
      horizTravel: horizTravel,
      latchPx: latch,
      latchEnd: latchEnd,
      exitHold: hold,
    };
  }

  function scrollState(root, baseboardHeight, exitHold) {
    const start = root.offsetTop;
    const metrics = travelMetrics(root, baseboardHeight, exitHold);
    const scrolled = clamp(window.scrollY - start, 0, metrics.totalTravel);
    if (scrolled <= metrics.horizTravel) {
      return {
        horizontalProgress: scrolled / metrics.horizTravel,
        yPx: 0,
        inHorizontal: true,
        inLatch: false,
        scrolled: scrolled,
      };
    }
    const trail = scrolled - metrics.horizTravel;
    const yPx = -Math.min(Math.max(0, baseboardHeight), trail);
    return {
      horizontalProgress: 1,
      yPx: yPx,
      inHorizontal: false,
      inLatch: scrolled <= metrics.latchEnd,
      scrolled: scrolled,
    };
  }

  /** Idle snap while in horizontal panels or the last-panel latch band. */
  function isInSnapRange(root, baseboardHeight, exitHold) {
    const start = root.offsetTop;
    const metrics = travelMetrics(root, baseboardHeight, exitHold);
    const y = window.scrollY;
    const pinEnd = start + metrics.totalTravel;
    if (y > pinEnd) return false;
    return y >= start && y <= start + metrics.latchEnd;
  }

  function panelProgress(index, panelCount) {
    const maxIndex = Math.max(0, panelCount - 1);
    if (maxIndex === 0) return 0;
    return clamp(index, 0, maxIndex) / maxIndex;
  }

  function targetYForIndex(root, index, panelCount, baseboardHeight, exitHold) {
    const start = root.offsetTop;
    const metrics = travelMetrics(root, baseboardHeight, exitHold);
    return start + panelProgress(index, panelCount) * metrics.horizTravel;
  }

  function applyTrackTransform(track, panelCount, progress, yPx) {
    const maxIndex = Math.max(0, panelCount - 1);
    const xPercent = -progress * maxIndex * 100;
    track.style.transform =
      'translate3d(' + xPercent + 'vw, ' + yPx + 'px, 0)';
  }

  function resolvePanelIndex(progress, panelCount, velocity) {
    const maxIndex = Math.max(0, panelCount - 1);
    if (maxIndex === 0) return 0;

    const scaled = progress * maxIndex;
    if (Math.abs(velocity) >= DIRECTIONAL_VELOCITY_PX) {
      if (velocity > 0) return clamp(Math.ceil(scaled - 0.001), 0, maxIndex);
      return clamp(Math.floor(scaled + 0.001), 0, maxIndex);
    }
    return clamp(Math.round(scaled), 0, maxIndex);
  }

  function buildSession() {
    const featured = Array.prototype.slice.call(document.querySelectorAll(FEATURED_SELECTOR));

    if (featured.length < 1) return null;

    const panels = featured;
    const panelCount = panels.length;
    const lastIndex = Math.max(0, panelCount - 1);
    const first = featured[0];
    const parent = first.parentNode;
    if (!parent) return null;

    const root = document.createElement('div');
    root.className = 'homepage-sidescroll';
    root.style.setProperty('--homepage-sidescroll-panel-count', String(panelCount));
    root.setAttribute('data-homepage-sidescroll', '');

    const sticky = document.createElement('div');
    sticky.className = 'homepage-sidescroll__sticky';

    const track = document.createElement('div');
    track.className = 'homepage-sidescroll__track';
    track.setAttribute('data-homepage-sidescroll-track', '');

    parent.insertBefore(root, first);
    root.appendChild(sticky);
    sticky.appendChild(track);

    panels.forEach(function (panel, index) {
      panel.classList.add('homepage-sidescroll__panel');
      if (index > 0) {
        const seam = document.createElement('div');
        seam.className = 'homepage-sidescroll__seam';
        seam.setAttribute('aria-hidden', 'true');
        panel.insertBefore(seam, panel.firstChild);
      }
      track.appendChild(panel);
    });

    document.body.classList.add('homepage-sidescroll-active');

    let baseboardHeight = measureAttachedBaseboardHeight();
    let exitHold = computeExitHoldPx();
    publishPinHeightVars(baseboardHeight, exitHold, root);

    let snapTimer = null;
    let snapping = false;
    let scrubRafId = 0;
    let snapRafId = 0;
    let settleRecheckId = 0;
    const initial = scrollState(root, baseboardHeight, exitHold);
    let displayProgress = initial.horizontalProgress;
    let displayY = initial.yPx;
    let lastScrollY = window.scrollY;
    let lastScrollTs = performance.now();
    let recentVelocity = 0;

    function cancelSnapTween() {
      if (snapRafId) {
        cancelAnimationFrame(snapRafId);
        snapRafId = 0;
      }
      snapping = false;
    }

    function lockToPanel(index) {
      const exactProgress = panelProgress(index, panelCount);
      const targetY = targetYForIndex(
        root,
        index,
        panelCount,
        baseboardHeight,
        exitHold,
      );
      window.scrollTo(0, targetY);
      displayProgress = exactProgress;
      displayY = 0;
      applyTrackTransform(track, panelCount, displayProgress, displayY);
      lastScrollY = window.scrollY;
    }

    function snapDurationMs(distancePx) {
      const speedFactor = clamp(Math.abs(recentVelocity) / VELOCITY_REF_PX, 0, 1);
      const distanceFactor = clamp(Math.abs(distancePx) / window.innerHeight, 0, 1);
      const t = Math.max(speedFactor, distanceFactor);
      return SNAP_DURATION_MIN_MS + (SNAP_DURATION_MAX_MS - SNAP_DURATION_MIN_MS) * t;
    }

    function tickScrub() {
      scrubRafId = 0;
      if (snapping) return;
      const target = scrollState(root, baseboardHeight, exitHold);
      const deltaP = target.horizontalProgress - displayProgress;
      const deltaY = target.yPx - displayY;
      if (Math.abs(deltaP) <= SCRUB_EPSILON) {
        displayProgress = target.horizontalProgress;
      } else {
        displayProgress += deltaP * SCRUB_LERP;
      }
      if (Math.abs(deltaY) <= Y_EPSILON) {
        displayY = target.yPx;
      } else {
        displayY += deltaY * SCRUB_LERP;
      }
      applyTrackTransform(track, panelCount, displayProgress, displayY);
      if (
        Math.abs(target.horizontalProgress - displayProgress) > SCRUB_EPSILON ||
        Math.abs(target.yPx - displayY) > Y_EPSILON
      ) {
        scrubRafId = requestAnimationFrame(tickScrub);
      }
    }

    function requestScrub() {
      if (snapping) return;
      if (!scrubRafId) scrubRafId = requestAnimationFrame(tickScrub);
    }

    function resolveIdleIndex(state) {
      if (state.inLatch || !state.inHorizontal) return lastIndex;
      return resolvePanelIndex(state.horizontalProgress, panelCount, recentVelocity);
    }

    function scheduleSettleRecheck() {
      if (settleRecheckId) clearTimeout(settleRecheckId);
      settleRecheckId = setTimeout(function () {
        settleRecheckId = 0;
        if (snapping) return;
        if (!isInSnapRange(root, baseboardHeight, exitHold)) return;
        const state = scrollState(root, baseboardHeight, exitHold);
        const index = state.inLatch ? lastIndex : resolvePanelIndex(state.horizontalProgress, panelCount, 0);
        const targetY = targetYForIndex(
          root,
          index,
          panelCount,
          baseboardHeight,
          exitHold,
        );
        if (Math.abs(window.scrollY - targetY) < SNAP_PIXEL_GATE) {
          lockToPanel(index);
          return;
        }
        scrollToPanel(index);
      }, 0);
    }

    function animateScrollTo(index, durationMs) {
      cancelSnapTween();
      snapping = true;
      if (scrubRafId) {
        cancelAnimationFrame(scrubRafId);
        scrubRafId = 0;
      }

      const targetY = targetYForIndex(
        root,
        index,
        panelCount,
        baseboardHeight,
        exitHold,
      );
      const exactProgress = panelProgress(index, panelCount);
      const startY = window.scrollY;
      const distance = targetY - startY;

      if (Math.abs(distance) < SNAP_PIXEL_GATE) {
        lockToPanel(index);
        snapping = false;
        scheduleSettleRecheck();
        return;
      }

      const startTs = performance.now();

      function step(now) {
        const elapsed = now - startTs;
        const t = clamp(elapsed / durationMs, 0, 1);
        const eased = easeOutCubic(t);
        const y = startY + distance * eased;
        window.scrollTo(0, y);
        const state = scrollState(root, baseboardHeight, exitHold);
        displayProgress = state.horizontalProgress;
        displayY = state.yPx;
        applyTrackTransform(track, panelCount, displayProgress, displayY);

        if (t < 1) {
          snapRafId = requestAnimationFrame(step);
          return;
        }

        snapRafId = 0;
        lockToPanel(index);
        displayProgress = exactProgress;
        displayY = 0;
        applyTrackTransform(track, panelCount, displayProgress, displayY);
        snapping = false;
        scheduleSettleRecheck();
      }

      snapRafId = requestAnimationFrame(step);
    }

    function scrollToPanel(index) {
      const targetY = targetYForIndex(
        root,
        index,
        panelCount,
        baseboardHeight,
        exitHold,
      );
      const durationMs = snapDurationMs(targetY - window.scrollY);
      animateScrollTo(index, durationMs);
    }

    function maybeSnapToIdle() {
      if (!isInSnapRange(root, baseboardHeight, exitHold)) return;
      const state = scrollState(root, baseboardHeight, exitHold);
      const index = resolveIdleIndex(state);
      const targetY = targetYForIndex(
        root,
        index,
        panelCount,
        baseboardHeight,
        exitHold,
      );
      if (Math.abs(window.scrollY - targetY) < SNAP_PIXEL_GATE) {
        lockToPanel(index);
        return;
      }
      scrollToPanel(index);
    }

    function onScroll() {
      const now = performance.now();
      const dy = window.scrollY - lastScrollY;
      const dt = Math.max(1, now - lastScrollTs);
      recentVelocity = (dy / dt) * 1000;
      lastScrollY = window.scrollY;
      lastScrollTs = now;

      requestScrub();

      if (snapping) return;
      if (snapTimer) clearTimeout(snapTimer);
      if (!isInSnapRange(root, baseboardHeight, exitHold)) {
        snapTimer = null;
        return;
      }
      snapTimer = setTimeout(function () {
        snapTimer = null;
        maybeSnapToIdle();
      }, SNAP_DELAY_MS);
    }

    function onResize() {
      baseboardHeight = measureAttachedBaseboardHeight();
      exitHold = computeExitHoldPx();
      publishPinHeightVars(baseboardHeight, exitHold, root);
      requestScrub();
      if (!isInSnapRange(root, baseboardHeight, exitHold)) {
        const state = scrollState(root, baseboardHeight, exitHold);
        displayProgress = state.horizontalProgress;
        displayY = state.yPx;
        applyTrackTransform(track, panelCount, displayProgress, displayY);
        return;
      }
      const state = scrollState(root, baseboardHeight, exitHold);
      const index = state.inLatch
        ? lastIndex
        : resolvePanelIndex(state.horizontalProgress, panelCount, 0);
      lockToPanel(index);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    applyTrackTransform(track, panelCount, displayProgress, displayY);
    syncGritSpanVars(root, exitHold);
    requestAnimationFrame(function () {
      document.dispatchEvent(new CustomEvent('homepage-sidescroll:layout'));
    });

    return {
      destroy: function () {
        if (snapTimer) clearTimeout(snapTimer);
        if (settleRecheckId) clearTimeout(settleRecheckId);
        cancelSnapTween();
        if (scrubRafId) cancelAnimationFrame(scrubRafId);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);

        panels.forEach(function (panel) {
          const seams = panel.querySelectorAll(':scope > .homepage-sidescroll__seam');
          for (let i = 0; i < seams.length; i++) {
            seams[i].parentNode.removeChild(seams[i]);
          }
          panel.classList.remove('homepage-sidescroll__panel');
        });

        featured.forEach(function (panel) {
          parent.insertBefore(panel, root);
        });

        if (root.parentNode) root.parentNode.removeChild(root);
        document.body.classList.remove('homepage-sidescroll-active');
        syncGritSpanVars();
        requestAnimationFrame(function () {
          document.dispatchEvent(new CustomEvent('homepage-sidescroll:layout'));
        });
      },
    };
  }

  function teardown() {
    if (!activeSession) return;
    activeSession.destroy();
    activeSession = null;
  }

  function sync() {
    const shouldRun =
      isIndexTemplate() && isDesktop() && !prefersReducedMotion() && !isDesignMode();

    if (!shouldRun) {
      teardown();
      syncGritSpanVars();
      return;
    }

    if (activeSession) return;
    activeSession = buildSession();
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  onReady(function () {
    sync();
    syncGritSpanVars();

    const desktopMq = window.matchMedia(DESKTOP_MQ);
    const reducedMq = window.matchMedia(REDUCED_MQ);

    function onMediaChange() {
      teardown();
      sync();
      syncGritSpanVars();
    }

    function onGritResize() {
      syncGritSpanVars();
    }

    window.addEventListener('resize', onGritResize);

    if (desktopMq.addEventListener) {
      desktopMq.addEventListener('change', onMediaChange);
      reducedMq.addEventListener('change', onMediaChange);
    } else if (desktopMq.addListener) {
      desktopMq.addListener(onMediaChange);
      reducedMq.addListener(onMediaChange);
    }
  });
})();
